import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FileService {
  private readonly previewableMimeTypes = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'text/plain'
  ];

  base64ToBlob(base64: string, mimeType: string): Blob {
    try {
      if (!base64) {
        return new Blob([], { type: mimeType });
      }
      const cleanBase64 = base64.includes(',') ? base64.split(',')[1] : base64;
      const byteString = atob(cleanBase64);
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const intArray = new Uint8Array(arrayBuffer);

      for (let i = 0; i < byteString.length; i++) {
        intArray[i] = byteString.codePointAt(i);
      }

      return new Blob([intArray], { type: mimeType });
    } catch (error) {
      console.error('Failed to convert Base64 to Blob:', error);
      return new Blob([], { type: mimeType });
    }
  }

  blobToBase64$(blob: Blob): Observable<string> {
    return new Observable<string>((subscriber) => {
      const reader = new FileReader();

      reader.onloadend = () => {
        const result = reader.result;

        if (typeof result === 'string') {
          subscriber.next(result);
          subscriber.complete();
        } else {
          subscriber.error(new Error('Failed to read Blob as Base64 string'));
        }
      };

      reader.onerror = (error) => subscriber.error(error);
      reader.readAsDataURL(blob);
    });
  }

  getMimeTypeFromFileName(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    switch (ext) {
      case 'pdf': return 'application/pdf';
      case 'xlsx': return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'jpg':
      case 'jpeg': return 'image/jpeg';
      case 'png': return 'image/png';
      case 'gif': return 'image/gif';
      case 'webp': return 'image/webp';
      case 'txt': return 'text/plain';
      case 'csv': return 'text/csv';
      case 'doc': return 'application/msword';
      case 'xls': return 'application/vnd.ms-excel';
      default: return 'application/octet-stream';
    }
  }

  downloadBlob(blob: Blob, fileName: string): void {
    const navigator = globalThis.navigator as any;

    if (navigator.msSaveOrOpenBlob) {
      navigator.msSaveOrOpenBlob(blob, fileName);
    } else {
      const url = this.getFileUrlFromBlob(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.dispatchEvent(new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: globalThis.window
      }));
      setTimeout(() => {
        globalThis.URL.revokeObjectURL(url);
        link.remove();
      }, 100);
    }
  }

  handleBlob(blob: Blob, fileName: string): void {
    const mimeType = this.getMimeTypeFromFileName(fileName);

    if (this.previewableMimeTypes.includes(mimeType)) {
      this.openBlobInNewTab(blob);
    } else {
      this.downloadBlob(blob, fileName);
    }
  }

  handleBase64File(base64: string, fileName: string): void {
    const mimeType = this.getMimeTypeFromFileName(fileName);
    const blob = this.base64ToBlob(base64, mimeType);
    this.handleBlob(blob, fileName);
  }

  downloadBase64File(base64: string, fileName: string): void {
    const mimeType = this.getMimeTypeFromFileName(fileName);
    const blob = this.base64ToBlob(base64, mimeType);
    this.downloadBlob(blob, fileName);
  }

  isPreviewable(fileName: string): boolean {
    const mimeType = this.getMimeTypeFromFileName(fileName);
    return this.previewableMimeTypes.includes(mimeType);
  }

  getFileIcon(type: string | undefined): string {
    if (!type) return 'insert_drive_file';
    const fileType = type.toLowerCase();
    if (fileType.includes('pdf')) return 'picture_as_pdf';
    if (fileType.includes('image') || /(jpg|jpeg|png|gif|webp)$/i.exec(fileType)) return 'image';
    if (fileType.includes('word') || fileType.includes('doc')) return 'description';
    if (fileType.includes('sheet') || fileType.includes('excel') || fileType.includes('csv') || fileType.includes('xls')) return 'table_chart';
    if (fileType.includes('text') || fileType.includes('txt')) return 'description';
    return 'insert_drive_file';
  }

  formatBytes(bytes: number, decimals = 2): string {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Number.parseFloat((bytes / Math.pow(k, i)).toFixed(Math.max(decimals, 0)))} ${sizes[i]}`;
  }

  openBlobInNewTab(blob: Blob): void {
    const url = this.getFileUrlFromBlob(blob);
    const newTab = window.open(url, '_blank');

    if (newTab) {
      newTab.onload = () => globalThis.URL.revokeObjectURL(url);
    }
  }

  getFileUrlFromBlob(blob: Blob): string {
    return globalThis.URL.createObjectURL(blob);
  }
}
